// @ts-strict-ignore
import { ProjectModel } from '@project/model/project.model';
import { Dictionary } from 'typescript-collections';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { SharedUserRightVO } from '@user/model/vo/shared-user-right.vo';
import { FriendSharedInfo } from '@project/components/permissions/friend-shared-info';
import { SharedObjectInfoVO, SharedProjectGroupInfoVO, SharedProjectInfoVO } from '@project/components/permissions/shared-info.vo';
import { FriendInfoVO } from '@project/components/permissions/friend-info.vo';
import { UserVO } from '@user/model/vo/user.vo';
import { FriendListEntryVO } from '@project/components/permissions/friend-list-entry.vo';
import { ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { FriendListService } from '@project/components/permissions/service/friend-list.service';
import { EventEmitter } from '@angular/core';
import { SharedObjectInfoDataService } from '../project/service/shared-object-info-data.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { CloneUtils } from '@shared/util/clone-utils';
import { Log } from '@shared/debug/log';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { UserModel } from '@user/model/user.model';

export class SharedUserRightsStorage {
  public localSharedRights: SharedUserRightVO[] = [];
  public loadedSharedRights: SharedUserRightVO[] = [];
}

export class UserFriendListContentPresenter {
  public userFriends: FriendSharedInfo[] = [];

  private _activeGroupNode: ProjectGroupVO | ProjectHeadVO = null;

  private _changedRightsForGroupIds: number[] = [];
  private _changedRightsForProjectIds: number[] = [];

  private _groupIdToSharedInfos: Dictionary<number, SharedUserRightsStorage> = new Dictionary<number, SharedUserRightsStorage>();
  private _projectIdToSharedInfos: Dictionary<number, SharedUserRightsStorage> = new Dictionary<number, SharedUserRightsStorage>();

  public friendListByCurrentUserLoaded: EventEmitter<any> = new EventEmitter();
  public sharedObjectInfoLoaded: EventEmitter<SharedProjectInfoVO | SharedProjectGroupInfoVO> = new EventEmitter<
    SharedProjectInfoVO | SharedProjectGroupInfoVO
  >();

  public get currentUser(): UserVO {
    return this.userModel.user;
  }

  public get changedRightsForGroupIds(): number[] {
    return this._changedRightsForGroupIds;
  }

  public get changedRightsForProjectIds(): number[] {
    return this._changedRightsForProjectIds;
  }

  constructor(
    private readonly alertHelper: AlertHelper,
    private readonly projectModel: ProjectModel,
    private readonly friendListService: FriendListService,
    private readonly sharedObjectInfoDataService: SharedObjectInfoDataService,
    private readonly userModel: UserModel,
  ) {
    this.loadFriendListByCurrentUser();
  }

  private loadFriendListByCurrentUser(): void {
    if (!this.userModel.user) {
      return;
    }
    this.friendListService
      .getFriendListByUserId()
      .toPromise()
      .then((friendList) => {
        this.getFriendList_success(friendList);
      })
      .catch((e) => {
        Log.info(e);
      });
  }

  public updateSharedRightsByGroupNode(group: ProjectGroupVO | ProjectHeadVO): Subject<any> {
    if (this._activeGroupNode === group) {
      return new BehaviorSubject(null);
    }
    this._activeGroupNode = group;
    if (!group) {
      this.applySharedRightsToActiveNode(null);
    } else {
      const sharedUserRightsStorage: SharedUserRightsStorage = this.getSharedRightsByGroupNode(group);
      if (!sharedUserRightsStorage || ArrayUtils.isEmpty(sharedUserRightsStorage.loadedSharedRights)) {
        return this.loadSharedObjectInfo(group);
      } else {
        this.applySharedRightsToActiveNode(sharedUserRightsStorage.localSharedRights);
      }
    }
    return new BehaviorSubject(null);
  }

  private loadSharedObjectInfo(object: ProjectGroupVO | ProjectHeadVO): Subject<any> {
    let promise: Promise<SharedProjectInfoVO>;
    const subject: Subject<any> = new Subject();
    if (object instanceof ProjectGroupVO) {
      promise = firstValueFrom(this.sharedObjectInfoDataService.getSharedProjectGroupInfoByGroupId(object.id));
    } else {
      promise =firstValueFrom(this.sharedObjectInfoDataService.getSharedProjectInfoByProjectId(object.id));
    }
    promise
      .then((sharedInfo) => {
        this.updateLoadedSharedRightsByNode(object, sharedInfo.userRights);
        this.sharedObjectInfoLoaded.emit(sharedInfo);
        subject.next(undefined);
        subject.complete();
      })
      .catch((e) => {
        this.loadSharedInfo_failure();
        subject.next(undefined);
        subject.complete();
      });
    return subject;
  }

  private applySharedRightsToActiveNode(sharedUserRights: SharedUserRightVO[], completeProperties: boolean = false): void {
    for (const friendSharedInfo of this.userFriends) {
      const sharedRight: SharedUserRightVO = this.findSharedUserRight(friendSharedInfo.userId, sharedUserRights);
      if (!sharedRight) {
        friendSharedInfo.isReadable = false;
        friendSharedInfo.isEditable = false;
      } else if (completeProperties) {
        friendSharedInfo.isReadable = sharedRight.isReadable;
        friendSharedInfo.isEditable = sharedRight.isEditable;
        friendSharedInfo.updateChildrenAvailable = sharedRight.updateChildrenAvailable;
      } else {
        friendSharedInfo.isReadable = sharedRight.isReadable && !sharedRight.isAutomaticApplied;
        friendSharedInfo.isEditable = sharedRight.isEditable && !sharedRight.isAutomaticApplied;
      }
    }
  }

  public getLocalChangedFriendInfoByProjectGroupId(projectGroupId: number): FriendSharedInfo[] {
    const result: FriendSharedInfo[] = [];
    if (projectGroupId && this._groupIdToSharedInfos.containsKey(projectGroupId)) {
      const sharedRights = this._groupIdToSharedInfos.getValue(projectGroupId).localSharedRights;
      for (const friendSharedInfo of this.userFriends) {
        const sharedRight: SharedUserRightVO = this.findSharedUserRight(friendSharedInfo.userId, sharedRights);
        if (sharedRight) {
          const newInfo: FriendSharedInfo = friendSharedInfo.clone();
          newInfo.isReadable = sharedRight.isReadable && !sharedRight.isAutomaticApplied;
          newInfo.isEditable = sharedRight.isEditable && !sharedRight.isAutomaticApplied;
          newInfo.updateChildrenAvailable = sharedRight.updateChildrenAvailable;
          result.push(newInfo);
        }
      }
    }
    return result;
  }

  private getSharedRightsByGroupNode(object: ProjectGroupVO | ProjectHeadVO): SharedUserRightsStorage {
    if (!object) {
      return null;
    }
    const sharedInfoStorage: Dictionary<number, SharedUserRightsStorage> =
      object instanceof ProjectGroupVO ? this._groupIdToSharedInfos : this._projectIdToSharedInfos;
    if (!sharedInfoStorage.containsKey(object.id)) {
      const sharedUserRightsStorage: SharedUserRightsStorage = new SharedUserRightsStorage();
      sharedUserRightsStorage.localSharedRights = [];
      sharedUserRightsStorage.loadedSharedRights = [];
      sharedInfoStorage.setValue(object.id, sharedUserRightsStorage);
    }
    return sharedInfoStorage.getValue(object.id);
  }

  public updateLoadedSharedRightsByNode(group: ProjectGroupVO | ProjectHeadVO, sharedRights: SharedUserRightVO[]): void {
    const sharedLoadedUserRightsStorage: SharedUserRightVO[] = this.getSharedRightsByGroupNode(group).loadedSharedRights;
    this.userFriends.forEach((friendSharedInfo: FriendSharedInfo) => {
      if (!this.findSharedUserRight(friendSharedInfo.id, sharedRights)) {
        const sharedRight = new SharedUserRightVO();
        sharedRight.userId = friendSharedInfo.id;
        sharedRight.isEditable = false;
        sharedRight.isReadable = false;
        sharedLoadedUserRightsStorage.push(sharedRight);
      }
    });
  }

  public processLoadedSharedInfo(sharedInfo: SharedObjectInfoVO, target: ProjectGroupVO | ProjectHeadVO): void {
    if (!sharedInfo || !sharedInfo.objectId || !target) {
      return;
    }
    let storage: Dictionary<number, SharedUserRightsStorage>;
    if (target instanceof ProjectGroupVO) {
      storage = this._groupIdToSharedInfos;
    } else if (target instanceof ProjectHeadVO) {
      storage = this._projectIdToSharedInfos;
    }

    const sharedUserRightsStorage: SharedUserRightsStorage = this.getUserRightsToStore(
      sharedInfo.userRights,
      target,
      storage.getValue(sharedInfo.objectId)
    );
    storage.setValue(sharedInfo.objectId, sharedUserRightsStorage);
    if (
      this._activeGroupNode &&
      ((this._activeGroupNode instanceof ProjectGroupVO && target instanceof ProjectGroupVO) ||
        (this._activeGroupNode instanceof ProjectHeadVO && target instanceof ProjectHeadVO)) &&
      this._activeGroupNode.id === sharedInfo.objectId
    ) {
      this.applySharedRightsToActiveNode(sharedUserRightsStorage.localSharedRights);
    }
  }

  private getUserRightsToStore(
    userRights: SharedUserRightVO[],
    target: ProjectGroupVO | ProjectHeadVO,
    localSharedUserRightsStorage: SharedUserRightsStorage
  ): SharedUserRightsStorage {
    let userRightsToStore: SharedUserRightVO[];
    if (ArrayUtils.isEmpty(userRights)) {
      userRightsToStore = [];
    } else {
      userRightsToStore = userRights.concat();
    }
    const parentProjectGroup = target instanceof ProjectHeadVO ? target.projectGroup : target.parentGroup;
    let parentProjectGroupRights: SharedUserRightVO[] = null;
    if (parentProjectGroup) {
      const parentProjectGroupRightsStorage: SharedUserRightsStorage = this.getSharedRightsByGroupNode(parentProjectGroup);
      parentProjectGroupRights = parentProjectGroupRightsStorage.localSharedRights;
    }

    loopByFriends: for (const friendSharedInfo of this.userFriends) {
      const parentProjectGroupRight: SharedUserRightVO = this.findSharedUserRight(friendSharedInfo.userId, parentProjectGroupRights);
      for (const userRight of userRightsToStore) {
        if (friendSharedInfo.userId === userRight.userId) {
          this.updateSharedRightByParent(userRight, parentProjectGroupRight);
          continue loopByFriends;
        }
      }
      const sharedUserRightVO: SharedUserRightVO = new SharedUserRightVO();
      sharedUserRightVO.userId = friendSharedInfo.userId;
      sharedUserRightVO.sharedFromUserId = this.userModel.user.id;
      this.updateSharedRightByParent(sharedUserRightVO, parentProjectGroupRight);
      userRightsToStore.push(sharedUserRightVO);
    }

    const sharedUserRightsStorage: SharedUserRightsStorage = new SharedUserRightsStorage();
    sharedUserRightsStorage.loadedSharedRights = CloneUtils.cloneSharedUserRightVOs(userRightsToStore);
    sharedUserRightsStorage.localSharedRights = userRightsToStore;
    this.updateRightsByLocallyChanged(userRightsToStore, localSharedUserRightsStorage);

    return sharedUserRightsStorage;
  }

  private updateSharedRightByParent(userRight: SharedUserRightVO, parentProjectGroupRight: SharedUserRightVO): void {
    if (!parentProjectGroupRight || !parentProjectGroupRight.updateChildrenAvailable || parentProjectGroupRight.isAutomaticApplied) {
      return;
    }
    userRight.isReadable = parentProjectGroupRight.isReadable;
    userRight.isEditable = parentProjectGroupRight.isEditable;
    userRight.updateChildrenAvailable = parentProjectGroupRight.updateChildrenAvailable;
  }

  private updateRightsByLocallyChanged(loadedRights: SharedUserRightVO[], localSharedUserRightsStorage: SharedUserRightsStorage): void {
    if (ArrayUtils.isEmpty(loadedRights) || !localSharedUserRightsStorage) {
      return;
    }
    for (const localRight of localSharedUserRightsStorage.localSharedRights) {
      for (const loadedRight of loadedRights) {
        if (localRight.userId === loadedRight.userId) {
          loadedRight.isReadable = localRight.isReadable;
          loadedRight.isEditable = localRight.isEditable;
          loadedRight.updateChildrenAvailable = localRight.updateChildrenAvailable;
          loadedRight.isAutomaticApplied = localRight.isAutomaticApplied;
          break;
        }
      }
    }
  }

  public getProjectSharedRightsToSave(): SharedProjectInfoVO[] {
    const sharedProjectInfos: SharedProjectInfoVO[] = [];
    for (const projectId of this._changedRightsForProjectIds) {
      const projectRightsStorage: SharedUserRightsStorage = this._projectIdToSharedInfos.getValue(projectId);
      if (
        projectRightsStorage &&
        !ArrayUtils.isEmpty(projectRightsStorage.localSharedRights) &&
        !this.isUserRightsEqual(projectRightsStorage.localSharedRights, projectRightsStorage.loadedSharedRights)
      ) {
        const sharedProjectInfo: SharedProjectInfoVO = new SharedProjectInfoVO();
        sharedProjectInfo.objectId = projectId;
        sharedProjectInfo.userRights = this.filterAutomaticSharedRights(projectRightsStorage.localSharedRights);
        sharedProjectInfos.push(sharedProjectInfo);
      }
    }
    return sharedProjectInfos;
  }

  public getProjectGroupSharedRightsToSave(): SharedProjectGroupInfoVO[] {
    const sharedProjectGroupInfos: SharedProjectGroupInfoVO[] = [];
    for (const groupId of this._changedRightsForGroupIds) {
      const groupRightsStorage: SharedUserRightsStorage = this._groupIdToSharedInfos.getValue(groupId);
      if (groupRightsStorage && !ArrayUtils.isEmpty(groupRightsStorage.localSharedRights)) {
        const sharedProjectGroupInfo: SharedProjectGroupInfoVO = new SharedProjectGroupInfoVO();
        sharedProjectGroupInfo.objectId = groupId;
        sharedProjectGroupInfo.userRights = this.filterAutomaticSharedRights(groupRightsStorage.localSharedRights);
        sharedProjectGroupInfos.push(sharedProjectGroupInfo);
      }
    }
    return sharedProjectGroupInfos;
  }

  private filterAutomaticSharedRights(sharedRights: SharedUserRightVO[]): SharedUserRightVO[] {
    const result: SharedUserRightVO[] = [];
    for (const sharedRight of sharedRights) {
      if (!sharedRight.isAutomaticApplied) {
        result.push(sharedRight);
      }
    }
    return result;
  }

  public cleanChangedRights(): void {
    for (const projectRightsStorage of this._projectIdToSharedInfos.values()) {
      const projectSharedRights: SharedUserRightVO[] = projectRightsStorage.localSharedRights;
      this.setUpdateChildrenAvailable(projectSharedRights, false);
      projectRightsStorage.loadedSharedRights = CloneUtils.cloneSharedUserRightVOs(projectSharedRights);
    }
    for (const groupRightsStorage of this._groupIdToSharedInfos.values()) {
      const groupSharedRights: SharedUserRightVO[] = groupRightsStorage.localSharedRights;
      this.setUpdateChildrenAvailable(groupSharedRights, false);
      groupRightsStorage.loadedSharedRights = CloneUtils.cloneSharedUserRightVOs(groupSharedRights);
    }
    this._changedRightsForProjectIds = [];
    this._changedRightsForGroupIds = [];
  }

  private setUpdateChildrenAvailable(sharedRights: SharedUserRightVO[], updateChildrenAvailable: boolean): void {
    if (ArrayUtils.isEmpty(sharedRights)) {
      return;
    }
    for (const sharedRight of sharedRights) {
      sharedRight.updateChildrenAvailable = updateChildrenAvailable;
    }
  }

  public isUserRightsChanged(): boolean {
    return (
      !ArrayUtils.isEmpty(this._changedRightsForGroupIds) ||
      !ArrayUtils.isEmpty(this._changedRightsForProjectIds) ||
      this.isCurrentUserRightsChanged()
    );
  }

  public isCurrentUserRightsChanged(): boolean {
    if (!this._activeGroupNode) {
      return false;
    }
    let savedUserRights: SharedUserRightVO[];
    const sharedUserRightsStorage: SharedUserRightsStorage = this.getSharedRightsByGroupNode(this._activeGroupNode);
    if (!sharedUserRightsStorage) {
      savedUserRights = [];
    } else {
      savedUserRights = sharedUserRightsStorage.localSharedRights;
    }
    const changedUserRights: SharedUserRightVO[] = this.collectUserRightsByFriendList();
    return !this.isUserRightsEqual(changedUserRights, savedUserRights);
  }

  private isUserRightsEqual(sourceRights: SharedUserRightVO[], targetRights: SharedUserRightVO[]): boolean {
    if (sourceRights === targetRights) {
      return true;
    }
    if (sourceRights.length !== targetRights.length) {
      return false;
    }
    for (const sourceRight of sourceRights) {
      const targetRight: SharedUserRightVO = this.findSharedUserRight(sourceRight.userId, targetRights);
      if (!targetRight || sourceRight.isReadable !== targetRight.isReadable || sourceRight.isEditable !== targetRight.isEditable) {
        return false;
      }
    }
    return true;
  }

  private collectUserRightsByFriendList(): SharedUserRightVO[] {
    const userRights: SharedUserRightVO[] = [];
    for (const friendSharedInfo of this.userFriends) {
      const sharedUserRight: SharedUserRightVO = new SharedUserRightVO();
      sharedUserRight.userId = friendSharedInfo.userId;
      sharedUserRight.sharedFromUserId = this.userModel.user.id;
      sharedUserRight.isReadable = friendSharedInfo.isReadable;
      sharedUserRight.isEditable = friendSharedInfo.isEditable;
      userRights.push(sharedUserRight);
    }
    return userRights;
  }

  private findSharedUserRight(userId: number, source: SharedUserRightVO[]): SharedUserRightVO {
    if (NumberUtils.isNotNumber(userId) || ArrayUtils.isEmpty(source)) {
      return null;
    }
    for (const userRight of source) {
      if (userId === userRight.userId) {
        return userRight;
      }
    }
    return null;
  }

  public addUserToFriendList(friendListEntry: FriendListEntryVO, object: ProjectGroupVO | ProjectHeadVO): void {
    const friendInfo: FriendInfoVO = friendListEntry.friendInfo;
    const friendSharedInfo: FriendSharedInfo = new FriendSharedInfo(friendInfo);
    friendSharedInfo.id = friendListEntry.id;
    friendSharedInfo.creationDate = friendListEntry.creationDate;
    this.userFriends.push(friendSharedInfo);
    if (object) {
      friendSharedInfo.isReadable = false;
      friendSharedInfo.isEditable = false;
    }
    this.addUserToStorage(friendSharedInfo, this._groupIdToSharedInfos);
    this.addUserToStorage(friendSharedInfo, this._projectIdToSharedInfos);
    this.refreshUserFriends();
  }

  private addUserToStorage(friendSharedInfo: FriendSharedInfo, storage: Dictionary<number, SharedUserRightsStorage>): void {
    for (const sharedUserRightStorage of storage.values()) {
      this.updateSharedUserRights(friendSharedInfo, sharedUserRightStorage.localSharedRights);
    }
  }

  public isFriendInList(friendName: string): boolean {
    for (const friendSharedInfo of this.userFriends) {
      const existingFriend: string = friendSharedInfo.username;
      if (friendName === existingFriend) {
        return true;
      }
    }
    return false;
  }

  public removeSharedFriendInfo(friendListEntry: FriendListEntryVO): void {
    const friendId: number = friendListEntry.friendInfo.id;
    this.removeSharedUserRightFromStorage(friendId, this._projectIdToSharedInfos);
    this.removeSharedUserRightFromStorage(friendId, this._groupIdToSharedInfos);
    let friendSharedInfo: FriendSharedInfo = null;
    for (const foundFriendSharedInfo of this.userFriends) {
      if (foundFriendSharedInfo.userId === friendListEntry.friendInfo.id) {
        friendSharedInfo = foundFriendSharedInfo;
        break;
      }
    }
    this.userFriends.splice(this.userFriends.indexOf(friendSharedInfo), 1);
    this.refreshUserFriends();
  }

  private removeSharedUserRightFromStorage(friendId: number, sharedInfoStorage: Dictionary<number, SharedUserRightsStorage>) {
    for (const sharedUserRightsStorage of sharedInfoStorage.values()) {
      this.removeSharedUserRightFromArray(friendId, sharedUserRightsStorage.loadedSharedRights);
      this.removeSharedUserRightFromArray(friendId, sharedUserRightsStorage.localSharedRights);
    }
  }

  private removeSharedUserRightFromArray(friendId: number, sharedUserRights: SharedUserRightVO[]): void {
    if (ArrayUtils.isEmpty(sharedUserRights)) {
      return;
    }
    let index: number = NaN;
    for (let i = 0; i < sharedUserRights.length; i++) {
      const sharedUserRightVO: SharedUserRightVO = sharedUserRights[i];
      if (sharedUserRightVO.userId === friendId) {
        index = i;
        break;
      }
    }
    if (NumberUtils.isNumber(index)) {
      sharedUserRights.splice(index, 1);
    }
  }

  public loadSharedInfo_failure(): void {
    this.alertHelper.showError('global.title.error', 'shareProjectPopup.alert.message.load_shared_rights_failure');
  }

  public getFriendList_success(friendListEntries: FriendListEntryVO[]): void {
    const friends: FriendSharedInfo[] = [];
    if (ArrayUtils.isNotEmpty(friendListEntries)) {
      for (const friendListEntry of friendListEntries) {
        if (!friendListEntry.friendInfo) {
          continue;
        }
        const friendSharedInfo: FriendSharedInfo = new FriendSharedInfo(friendListEntry.friendInfo);
        friendSharedInfo.id = friendListEntry.id;
        friendSharedInfo.creationDate = friendListEntry.creationDate;
        friends.push(friendSharedInfo);
      }
    }
    this.userFriends = friends;
    this.userFriends.sort(this.userCompareFunction);
    this.friendListByCurrentUserLoaded.emit();
  }

  private userCompareFunction(userA: FriendSharedInfo, userB: FriendSharedInfo): number {
    let compare: number = NumberUtils.numericCompare(new Date(userB.creationDate).getTime(), new Date(userA.creationDate).getTime());
    if (compare === 0) {
      compare = NumberUtils.numericCompare(userB.id, userA.id);
    }
    return compare;
  }

  public applyChangedSharedUserRightsToUploadedNodes(selectedNode: ProjectGroupVO | ProjectHeadVO, friendSharedInfos: FriendSharedInfo[] = null): void {
    if (!selectedNode) {
      return;
    }

    const processedFriendSharedInfo: FriendSharedInfo[] = ArrayUtils.isEmpty(friendSharedInfos) ? this.userFriends : friendSharedInfos;
    const childNodes: (ProjectGroupVO | ProjectHeadVO)[] = this.getChildDependentNodes(selectedNode);
    const sharedUserRightsStorage: SharedUserRightsStorage = this.getSharedRightsByGroupNode(selectedNode);
    for (const friendSharedInfo of processedFriendSharedInfo) {
      this.updateSharedUserRights(friendSharedInfo, sharedUserRightsStorage.localSharedRights);
      if (friendSharedInfo.updateChildrenAvailable) {
        this.applySharedUserRightsToChildNodes(childNodes, friendSharedInfo);
      }
    }
    this.updateStorageByLocallyChangedNodeId(selectedNode, sharedUserRightsStorage);
  }

  private updateStorageByLocallyChangedNodeId(node: ProjectGroupVO | ProjectHeadVO, sharedUserRightsStorage: SharedUserRightsStorage): void {
    let storage: number[];
    if (node instanceof ProjectHeadVO) {
      storage = this._changedRightsForProjectIds;
    } else {
      storage = this._changedRightsForGroupIds;
    }

    if (sharedUserRightsStorage) {
      if (this.isUserRightsEqual(sharedUserRightsStorage.loadedSharedRights, sharedUserRightsStorage.localSharedRights)) {
        /*if(storage.indexOf(node.id) > -1)
        {
          storage.splice(storage.indexOf(node.id), 1);
        }*/
      } else if (storage.indexOf(node.id) < 0) {
        storage.push(node.id);
      }
    }
  }

  private getChildDependentNodes(
    selectedNode: ProjectGroupVO | ProjectHeadVO,
    nodes: (ProjectGroupVO | ProjectHeadVO)[] = []
  ): (ProjectGroupVO | ProjectHeadVO)[] {
    if (!selectedNode) {
      return nodes;
    }
    if (ArrayUtils.isEmpty(nodes)) {
      nodes = [];
    }
    if (selectedNode instanceof ProjectGroupVO) {
      if (ArrayUtils.isNotEmpty(selectedNode.groups)) {
        for (const child of selectedNode.groups) {
          this.getChildDependentNodes(child, nodes);
          nodes.push(child);
        }
      }
      if (ArrayUtils.isNotEmpty(selectedNode.projects)) {
        for (const child of selectedNode.projects) {
          nodes.push(child);
        }
      }
    }
    return nodes;
  }

  public getParentDependentNodes(groupNode: ProjectGroupVO | ProjectHeadVO): (ProjectGroupVO | ProjectHeadVO)[] {
    if (!groupNode) {
      return [];
    }
    const nodes: (ProjectGroupVO | ProjectHeadVO)[] = [];
    let node: ProjectGroupVO = groupNode instanceof ProjectHeadVO ? groupNode.projectGroup : groupNode;
    if (groupNode instanceof ProjectHeadVO) {
      nodes.push(groupNode.projectGroup);
    }
    while (node.parentGroup) {
      node = node.parentGroup;
      nodes.push(node);
    }
    return nodes;
  }

  private applySharedUserRightsToChildNodes(nodes: (ProjectGroupVO | ProjectHeadVO)[], friendSharedInfo: FriendSharedInfo): void {
    for (const node of nodes) {
      const sharedUserRightsStorage: SharedUserRightsStorage = this.getSharedRightsByGroupNode(node);
      this.updateSharedUserRights(friendSharedInfo, sharedUserRightsStorage.localSharedRights);
      this.updateStorageByLocallyChangedNodeId(node, sharedUserRightsStorage);
    }
  }

  private updateSharedUserRights(friendSharedInfo: FriendSharedInfo, sharedUserRights: SharedUserRightVO[]): void {
    if (!friendSharedInfo || !sharedUserRights) {
      return;
    }
    const friendId: number = friendSharedInfo.userId;
    let sharedUserRight: SharedUserRightVO = this.findSharedUserRight(friendId, sharedUserRights);
    if (!sharedUserRight) {
      sharedUserRight = new SharedUserRightVO();
      sharedUserRight.userId = friendId;
      sharedUserRight.sharedFromUserId = this.userModel.user.id;
      sharedUserRights.push(sharedUserRight);
    }
    sharedUserRight.isReadable = friendSharedInfo.isReadable;
    sharedUserRight.isEditable = friendSharedInfo.isEditable;
    sharedUserRight.isAutomaticApplied = false;
    sharedUserRight.updateChildrenAvailable = friendSharedInfo.updateChildrenAvailable;
  }

  public removeModifiedSharedRightsFromFriendList(): void {
    this.cleanChangedRights();
    if (!this._activeGroupNode) {
      return;
    }
    const savedUserRights: SharedUserRightVO[] = this.getSharedRightsByGroupNode(this._activeGroupNode).localSharedRights;
    this.applySharedRightsToActiveNode(savedUserRights, true);
  }

  public getPreselectedProjectGroupId(): number {
    const currentProject: ProjectVO = this.projectModel.currentProject;
    let projectGroup: ProjectGroupVO = null;
    if (currentProject && !this.projectModel.isNewConfiguration && this.userModel.user.id === currentProject.projectGroup?.groupOwner?.userId) {
      projectGroup = currentProject.projectGroup;
    }
    return !projectGroup ? NaN : projectGroup.id;
  }

  public getLocalSharedRightsForUser(
    userId: number,
    localSharedIds: number[],
    targetNode: ProjectGroupVO | ProjectHeadVO
  ): Dictionary<number, SharedUserRightVO> {
    if (NumberUtils.isNotNumber(userId) || ArrayUtils.isEmpty(localSharedIds) || !targetNode) {
      return new Dictionary<number, SharedUserRightVO>();
    }

    let storage: Dictionary<number, SharedUserRightsStorage>;
    if (targetNode instanceof ProjectGroupVO) {
      storage = this._groupIdToSharedInfos;
    } else if (targetNode instanceof ProjectHeadVO) {
      storage = this._projectIdToSharedInfos;
    }

    const localSharedRights: Dictionary<number, SharedUserRightVO> = new Dictionary<number, SharedUserRightVO>();
    for (const id of localSharedIds) {
      const sharedUserRightsStorage: SharedUserRightsStorage = storage.getValue(id);
      if (sharedUserRightsStorage) {
        const sharedRight: SharedUserRightVO = this.findSharedUserRight(userId, sharedUserRightsStorage.localSharedRights);
        if (sharedRight) {
          localSharedRights.setValue(id, sharedRight);
        }
      }
    }
    return localSharedRights;
  }

  public removeSharedRightsByNode(node: ProjectGroupVO | ProjectHeadVO): void {
    if (!node) {
      return;
    }
    const sharedInfoStorage: Dictionary<number, SharedUserRightsStorage> =
      node instanceof ProjectGroupVO ? this._groupIdToSharedInfos : this._projectIdToSharedInfos;
    sharedInfoStorage.remove(node.id);
  }

  private refreshUserFriends(): void {
    const temp: FriendSharedInfo[] = this.userFriends.concat();
    this.userFriends = [];
    this.userFriends = temp;
    this.userFriends.sort(this.userCompareFunction);
  }
}
